import { useCreateListing } from "@/hooks/reservoir/use-create-listing";
import { useGetAssetBalance } from "@/hooks/use-get-asset-balance";
import { useGetErc20Balance } from "@/hooks/use-get-erc-20-balance";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useStringWithPlusMinus } from "@/hooks/useStringWithPlusMinus";
import { trpc } from "@/util/trpc";
import { usdcContractAddress } from "@talentir/shared/abis/erc-20";
import { SHARE_TO_PERCENT_CONVERSION } from "@talentir/shared/config";
import Big from "big.js";
import { useMemo } from "react";
import { toast } from "sonner";
import type { Asset } from "../order-dialog-v2";
import { OrderCommon } from "./common";

export function CreateListing({ asset, onComplete }: { asset: Asset; onComplete: () => void }) {
    const assetBalance = useGetAssetBalance(asset.id);
    const { createListing, isLoading } = useCreateListing();
    const { data: feeData } = trpc.lambda.utils.getSellFees.useQuery();
    const { user } = useGetLoggedInUser();
    const usdcBalance = useGetErc20Balance(usdcContractAddress, "USD", user?.id);

    const [percent, , strippedPercent, setRawPercent] = useStringWithPlusMinus("-");
    const [cost, , strippedCost, setRawCost] = useStringWithPlusMinus("+");

    const price = useMemo(() => {
        try {
            return +Big(strippedCost).div(Big(strippedPercent)).toString();
        } catch {
            return 0;
        }
    }, [strippedPercent, strippedCost]);

    const percentError = useMemo(() => {
        try {
            const percentBig = Big(strippedPercent);
            const assetBalanceBig = Big(assetBalance.assetBalanceString ?? 0);

            if (percentBig.gt(100)) {
                return "You cannot offer more than 100%";
            }

            if (percentBig.gt(assetBalanceBig)) {
                return "You cannot offer more than you own";
            }

            return undefined;
        } catch {
            return undefined;
        }
    }, [assetBalance.assetBalanceString, strippedPercent]);

    const feeString = useMemo(() => {
        if (feeData?.artistRoyalty == null || +strippedCost === 0) {
            return undefined;
        }
        const feeCost = +strippedCost * feeData.artistRoyalty;

        return `Artist Receives: ${feeCost.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
        })} USD`;
    }, [feeData?.artistRoyalty, strippedCost]);

    async function prepareAndCreateListing() {
        try {
            let costRaw: bigint | undefined;
            let quantityRaw: bigint | undefined;
            try {
                costRaw = BigInt(Big(strippedCost).mul(Big(10).pow(6)).round().toString());
            } catch {
                toast.error("Price Error", { description: "Please enter a valid price" });
                return;
            }

            try {
                quantityRaw = BigInt(Big(strippedPercent).mul(Big(SHARE_TO_PERCENT_CONVERSION)).round().toString());
            } catch {
                toast.error("Quantity Error", { description: "Please enter a valid amount" });
                return;
            }

            await createListing(asset.blockchainTokenId ?? "", quantityRaw, costRaw);
            onComplete();
        } catch {}
    }

    return (
        <OrderCommon
            asset={asset}
            cta="Create Listing"
            buttonLabel="Create Listing"
            isLoading={isLoading}
            topCurrency="%"
            topLeftInfo={`Current Ownership: ${assetBalance.assetBalanceString}%`}
            topLabel="You Offer For Sale %"
            topValue={percent}
            onTopValueChange={setRawPercent}
            topDecimals={4}
            topError={percentError}
            bottomCurrency="$"
            bottomLeftInfo={`Current Balance: ${usdcBalance?.balanceFormatted ?? "$0.00"}`}
            bottomRightInfo={feeString}
            bottomLabel="You Want USD"
            bottomValue={cost}
            onBottomValueChange={setRawCost}
            bottomDecimals={2}
            priceForOnePercent={`${price.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
            })}`}
            onComplete={() => {
                void prepareAndCreateListing();
            }}
        />
    );
}
